import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { ActionBackOfficeResponse } from '../../../../../core/models/api.model';
import { MapperRegisterMemberCardStaffService } from '../../../../../core/mappers/cityparking/mapper-register-member-card-staff.service';
import {
  EvStationManualEntryPayload,
  EvStationManualEntryVehicleOption,
} from '../../../../../core/models/cityparking/ev-station-monitor.model';
import { StaffLineUserOption } from '../../../../../core/models/cityparking/register-member-card-staff.model';
import { CityParkingUserVehicleListResponse } from '../../../../../core/models/lineOA.model';
import { ApiService } from '../../../../../core/services/api.service';
import { ApiEvStationMonitorService } from '../../../../../core/services/api/cityparking/api-ev-station-monitor.service';
import { ApiLineOaService } from '../../../../../core/services/api/line-oa/api-line-oa.service';
import { PssConfirmModalComponent } from '../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';

@Component({
  selector: 'app-modal-ev-station-manual-entry',
  standalone: true,
  imports: [CommonModule, FormsModule, PssConfirmModalComponent, PssLoadingComponent],
  templateUrl: './modal-ev-station-manual-entry.component.html',
})
export class ModalEvStationManualEntryComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() gateId = 0;
  @Input() gateName = '';
  @Input() userId = 0;
  @Input() menuId = 0;
  @Output() onClose = new EventEmitter<void>();
  @Output() onSuccess = new EventEmitter<void>();

  private readonly apiLineOa = inject(ApiLineOaService);
  private readonly apiEv = inject(ApiEvStationMonitorService);
  private readonly apiService = inject(ApiService);
  private readonly lineMapper = inject(MapperRegisterMemberCardStaffService);
  private readonly toast = inject(ToastService);

  isSaving = false;
  isSearchingLine = false;
  isLoadingVehicles = false;
  showConfirm = false;

  openDropdown: 'line' | 'vehicle' | null = null;
  searchLineText = '';
  searchVehicleText = '';
  lineOptions: StaffLineUserOption[] = [];
  vehicleOptions: EvStationManualEntryVehicleOption[] = [];
  selectedLine: StaffLineUserOption | null = null;
  selectedVehicle: EvStationManualEntryVehicleOption | null = null;

  private lineSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private blurTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.resetForm();
    }
    if (changes['isOpen'] && !this.isOpen) {
      this.resetForm();
    }
  }

  get filteredVehicles(): EvStationManualEntryVehicleOption[] {
    const q = this.searchVehicleText.trim().toLowerCase();
    if (!q) return this.vehicleOptions;
    if (this.selectedVehicle && this.searchVehicleText === this.selectedVehicle.label) {
      return this.vehicleOptions;
    }
    return this.vehicleOptions.filter(
      (v) => v.label.toLowerCase().includes(q) || v.plateNumber.toLowerCase().includes(q),
    );
  }

  closeModal(): void {
    if (this.isSaving) return;
    this.onClose.emit();
  }

  onInputFocus(field: 'line' | 'vehicle'): void {
    this.openDropdown = field;
  }

  closeDropdowns(): void {
    if (this.blurTimer) clearTimeout(this.blurTimer);
    this.blurTimer = setTimeout(() => {
      this.openDropdown = null;
      this.revertTypedDropdowns();
    }, 150);
  }

  onLineSearchChange(): void {
    if (this.lineSearchTimer) clearTimeout(this.lineSearchTimer);
    this.selectedLine = null;
    this.selectedVehicle = null;
    this.vehicleOptions = [];
    this.searchVehicleText = '';
    this.lineSearchTimer = setTimeout(() => this.searchLineUsers(), 300);
  }

  selectLineUser(item: StaffLineUserOption): void {
    this.selectedLine = item;
    this.searchLineText = item.fullName || item.name;
    this.openDropdown = null;
    this.selectedVehicle = null;
    this.searchVehicleText = '';
    this.loadVehicles(item.uid);
  }

  selectVehicle(item: EvStationManualEntryVehicleOption): void {
    this.selectedVehicle = item;
    this.searchVehicleText = item.label;
    this.openDropdown = null;
  }

  askSubmit(): void {
    const error = this.validate();
    if (error) {
      this.toast.error(error);
      return;
    }
    this.showConfirm = true;
  }

  closeConfirm(): void {
    this.showConfirm = false;
  }

  confirmSubmit(): void {
    this.showConfirm = false;
    this.submit();
  }

  private validate(): string | null {
    if (!this.userId || !this.menuId) return 'ไม่พบสิทธิ์เมนูหรือผู้ใช้';
    if (!this.gateId) return 'ไม่พบช่องจอด';
    if (!this.selectedLine?.uid) return 'กรุณาเลือกลูกค้า LINE';
    if (!this.selectedVehicle?.vehicleId) return 'กรุณาเลือกทะเบียนรถ';
    return null;
  }

  private submit(): void {
    if (!this.selectedLine || !this.selectedVehicle) return;

    const payload: EvStationManualEntryPayload = {
      userId: this.userId,
      menuId: this.menuId,
      gateId: this.gateId,
      lineUid: this.selectedLine.uid,
      vehicleId: this.selectedVehicle.vehicleId,
      license: this.selectedVehicle.plateNumber,
    };

    this.isSaving = true;
    this.apiEv.createEvStationManualEntry(payload).subscribe({
      next: (res: ActionBackOfficeResponse) => {
        this.isSaving = false;
        const raw = res as unknown as Record<string, unknown>;
        const ok = res.isSuccess === true || raw['IsSuccess'] === true;
        const message = String(res.message ?? raw['Message'] ?? '');
        if (ok) {
          this.toast.success(message || 'ทำรายการเข้าเรียบร้อย');
          this.onSuccess.emit();
          this.onClose.emit();
          return;
        }
        this.toast.error(message || 'ทำรายการเข้าไม่สำเร็จ');
      },
      error: (err) => {
        console.error(err);
        this.isSaving = false;
        this.toast.error('ทำรายการเข้าไม่สำเร็จ');
      },
    });
  }

  private searchLineUsers(): void {
    const text = this.searchLineText.trim();
    if (!text) {
      this.lineOptions = [];
      return;
    }
    const searchField = /^\d+$/.test(text) ? 'phone' : 'name';
    this.isSearchingLine = true;
    this.apiLineOa
      .getUserLineOAForTable({
        page: 1,
        pageSize: 20,
        searchField,
        searchText: text,
      })
      .subscribe({
        next: (res) => {
          this.lineOptions = this.lineMapper.mapLineUsers(res.data ?? []);
          this.isSearchingLine = false;
          this.openDropdown = 'line';
        },
        error: () => {
          this.isSearchingLine = false;
          this.toast.error('ค้นหา LINE OA ไม่สำเร็จ');
        },
      });
  }

  private loadVehicles(uid: string): void {
    const cpUserId = this.selectedLine?.cpUserId ?? 0;
    if (!uid || cpUserId <= 0) {
      this.vehicleOptions = [];
      return;
    }
    this.isLoadingVehicles = true;
    // ใช้ getUserLineProfile (มีจริงใน Platform API) — ไม่ใช้ getVehiclelistresponse ที่ไม่มี endpoint
    this.apiService.getCityParkingUserProfile(cpUserId, uid).subscribe({
      next: (profile) => {
        const rows = profile?.cityParkingUserVehicleListResponses ?? [];
        this.vehicleOptions = this.mapVehiclesFromProfile(rows);
        this.isLoadingVehicles = false;
        if (this.vehicleOptions.length === 0) {
          this.toast.error('ไม่พบรถของลูกค้าคนนี้');
        }
      },
      error: (err) => {
        console.error(err);
        this.isLoadingVehicles = false;
        this.vehicleOptions = [];
        this.toast.error('โหลดทะเบียนรถไม่สำเร็จ');
      },
    });
  }

  private mapVehiclesFromProfile(
    rows: CityParkingUserVehicleListResponse[],
  ): EvStationManualEntryVehicleOption[] {
    return (rows ?? [])
      .map((row) => {
        const raw = row as CityParkingUserVehicleListResponse & Record<string, unknown>;
        const vehicleId = Number(row.id ?? raw['Id'] ?? 0);
        const plate = String(row.plateNumber ?? raw['PlateNumber'] ?? '').trim();
        const province = String(row.provinceName ?? raw['ProvinceName'] ?? '').trim();
        const typeName = String(row.vehicleTypeName ?? raw['VehicleTypeName'] ?? '').trim();
        const ownerTag = row.isOwner === false ? 'แชร์' : '';
        const labelParts = [plate, province, typeName, ownerTag].filter((x) => !!x);
        return {
          vehicleId,
          plateNumber: plate,
          label: labelParts.join(' · ') || plate,
        };
      })
      .filter((v) => v.vehicleId > 0 && !!v.plateNumber);
  }

  private revertTypedDropdowns(): void {
    if (this.selectedLine) {
      this.searchLineText = this.selectedLine.fullName || this.selectedLine.name;
    }
    if (this.selectedVehicle) {
      this.searchVehicleText = this.selectedVehicle.label;
    }
  }

  private resetForm(): void {
    this.isSaving = false;
    this.isSearchingLine = false;
    this.isLoadingVehicles = false;
    this.showConfirm = false;
    this.openDropdown = null;
    this.searchLineText = '';
    this.searchVehicleText = '';
    this.lineOptions = [];
    this.vehicleOptions = [];
    this.selectedLine = null;
    this.selectedVehicle = null;
  }
}
